import { hub } from "./hub";

// A website's admin API (Site Admin API v1), called straight from the browser with a site token from the hub
// (5 minutes; asked for again a minute before it runs out). The website checks the token and the permissions in it.

export type SiteApi = ReturnType<typeof siteApi>;

export class SiteError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export function siteApi(siteId: string) {
  let token: Promise<{ token: string; expiresAt: number; apiBase: string }> | undefined;
  const fresh = () => {
    const current = token;
    return current?.then((t) => (t.expiresAt - Date.now() > 60_000 ? t : null)).catch(() => null) ?? Promise.resolve(null);
  };
  async function auth() {
    const valid = await fresh();
    if (valid) return valid;
    token = hub.siteToken(siteId);
    return token;
  }

  async function send(method: string, path: string, body?: BodyInit, contentType?: string): Promise<Response> {
    const { token: t, apiBase } = await auth();
    const headers: Record<string, string> = { Authorization: `Bearer ${t}` };
    if (contentType) headers["Content-Type"] = contentType;
    return fetch(`${apiBase}${path}`, { method, headers, body });
  }

  async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
    let res: Response;
    try {
      res = await send(method, path, body === undefined ? undefined : JSON.stringify(body), body === undefined ? undefined : "application/json");
    } catch {
      throw new SiteError("The website didn't answer. Check your connection and try again.", 0);
    }
    if (res.status === 401) token = undefined; // a fresh token next time
    const data = (await res.json().catch(() => null)) as (T & { ok?: boolean; error?: string }) | null;
    if (!data) throw new SiteError(`The website answered ${res.status}.`, res.status);
    if (!res.ok && res.status !== 422) throw new SiteError(data.error ?? `The website answered ${res.status}.`, res.status);
    return data;
  }

  return {
    siteId,
    get: <T>(path: string) => json<T>("GET", path),
    post: <T>(path: string, body?: unknown) => json<T>("POST", path, body ?? {}),
    put: <T>(path: string, body: unknown) => json<T>("PUT", path, body),
    patch: <T>(path: string, body: unknown) => json<T>("PATCH", path, body),
    del: <T>(path: string) => json<T>("DELETE", path),
    /** A file upload (multipart): the browser sends its length, which the website requires. */
    async upload<T>(path: string, form: FormData): Promise<T> {
      const res = await send("POST", path, form);
      const data = (await res.json().catch(() => null)) as T | null;
      if (!data) throw new SiteError(`The website answered ${res.status}.`, res.status);
      return data;
    },
    /** Raw bytes (a statistics file), or null when there is none. */
    async bytes(path: string): Promise<ArrayBuffer | null> {
      const res = await send("GET", path);
      if (res.status === 404) return null;
      if (!res.ok) throw new SiteError(`The website answered ${res.status}.`, res.status);
      return res.arrayBuffer();
    },
  };
}
